import Plot from "components/plotly/LocalisedPlot";
import { useTheme } from "@mui/material";
import { LegendHint } from "../LegendHint";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { setSelectedDateFromPlotClick } from "../plotting";

interface EditingSeries {
  heat_date: string;
  product_group_name: string;
  heats_edited: number;
  total_mass_changed: number;
  avg_tons_changed_per_heat: number | null;
}

type EditingPlotProps = Readonly<{
  series: readonly EditingSeries[];
  selectedProductGroup: string;
  setSelectedDate: (date: Date) => void;
}>;

export const EditingPlot = ({
  series,
  selectedProductGroup,
  setSelectedDate,
}: EditingPlotProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const filteredSeries =
    selectedProductGroup === "all"
      ? series.filter((s) => s.product_group_name === "All")
      : series.filter((s) => s.product_group_name === selectedProductGroup);

  const sortedSeries = [...filteredSeries].sort(
    (a, b) => new Date(a.heat_date).getTime() - new Date(b.heat_date).getTime()
  );

  const dates = sortedSeries.map((s) => s.heat_date);
  const avgMasses = sortedSeries.map((s) => s.avg_tons_changed_per_heat);

  const rollingAvg = avgMasses.map((_, idx, arr) => {
    const windowStart = Math.max(0, idx - 6);
    const window = arr
      .slice(windowStart, idx + 1)
      .filter((value): value is number => value !== null);
    if (window.length === 0) {
      return null;
    }
    const sum = window.reduce((total, value) => total + value, 0);
    return sum / window.length;
  });

  const data: Partial<PlotData>[] = [
    {
      x: dates,
      y: avgMasses,
      type: "scatter",
      mode: "lines+markers",
      name: t("avgTonsChangedPerHeat"),
      line: {
        color: theme.palette.data.blue,
        width: 2,
      },
      marker: {
        size: 6,
        color: theme.palette.data.blue,
      },
    },
    {
      x: dates,
      y: rollingAvg,
      type: "scatter",
      mode: "lines",
      name: t("rollingWeeklyAverage"),
      line: {
        color: theme.palette.data.red,
        width: 3,
        dash: "dash",
      },
    },
  ];

  return (
    <>
      <Plot
        data={data}
        layout={{
          autosize: true,
          xaxis: {
            title: { text: t("date") },
            type: "date",
          },
          yaxis: {
            title: { text: `${t("avgTonsChangedPerHeat")} (${t("tons")})` },
          },
          margin: {
            l: 60,
            r: 30,
            t: 30,
            b: 60,
          },
          hovermode: "x unified",
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
        }}
        style={{ width: "100%", height: "500px" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </>
  );
};
